import { useLingui } from "@lingui/react";
import {
  Icon,
  ScrollableContainer,
  useParsedSearchParams,
} from "@fidelix/fx-miranda";
import { Outlet } from "react-router-dom";
import { css, CSSProperties, styled } from "styled-components";
import { usePersistSearchParams } from "~utils/url";
import { Button } from "react-aria-components";
import useMeasure from "react-use-measure";
import { PropsWithChildren } from "react";
import { getActiveRoute, MainRoute, Navbar } from "./nav-bar";
import { QueryBoundary } from "./query-boundary";
import { useWindowResizeEvent } from "../utils/observe";

const COLLAPSE_BUTTON_WIDTH = 18;
const COLLAPSE_BUTTON_HEIGHT = 72;

export let sidePanelWidth = 0;

// Params in this list are carried over between all routes, unlike the
// per-pathname restoration handled by `restoreSearchParamsLoader`.
// These are all hierarchy tree related params for now.

// NOTE: The state of GLOBAL_SEARCH_PARAMS will always overwrite
// local page params if the route is using global params

// A route can opt out of using global params with the handle propert, like so:
// { path: "history", handle: { excludeGlobalSearchParams: true }, ... }
const GLOBAL_SEARCH_PARAMS = [
  "currentNode",
  "node",
  "device",
  "level",
  "tSearch",
];

const allowScrollX: { [key in MainRoute]: boolean } = {
  alarms: true,
  devices: true,
  graphics: false,
  history: false,
  energy: true,
  logs: true,
  menus: true,
  overview: true,
  points: true,
  schedule: true,
};

export function PageLayout() {
  useLingui();
  usePersistSearchParams(GLOBAL_SEARCH_PARAMS);

  return (
    <Layout>
      <Navbar />
      {/* NOTE: this id is used to calculate the width of the main content area */}
      <Main id="main">
        <QueryBoundary>
          <Outlet />
        </QueryBoundary>
      </Main>
    </Layout>
  );
}

type SidePanelProps = {
  showBorder?: boolean;
  isScrollable?: boolean;
  isCollapsible?: boolean;
  hasTree?: boolean;
  children?: React.ReactNode;
  style?: CSSProperties;
};
export function SidePanelLayout({
  children,
  showBorder,
  isScrollable,
  isCollapsible,
  hasTree,
  style,
}: SidePanelProps) {
  const { parsedParams, setSearchParams } = useParsedSearchParams({
    isCollapsed: { type: "string", defaultValue: undefined },
  });
  const [measureRef, { width, top }] = useMeasure();
  const { innerHeight } = useWindowResizeEvent();
  const isCollapsed = parsedParams.isCollapsed === "true";
  const left = isCollapsed ? COLLAPSE_BUTTON_WIDTH : width;

  const height = top ? innerHeight - top : innerHeight - 200;
  sidePanelWidth = width;

  const onPressCollapse = () => {
    setSearchParams(
      (params) => {
        if (!isCollapsed) {
          params.set("isCollapsed", "true");
        } else {
          params.delete("isCollapsed");
        }
      },
      { replace: true },
    );
    // Dispatch resize event so all responsive elements get resized accordingly
    window.dispatchEvent(new Event("resize"));
  };

  return (
    <>
      {showBorder && <Border style={{ left }} $showBorder={showBorder} />}
      {isCollapsible && (
        <CollapseButton
          onPress={onPressCollapse}
          style={{
            left: left - COLLAPSE_BUTTON_WIDTH / 2,
            top: innerHeight / 2 - COLLAPSE_BUTTON_HEIGHT / 2,
          }}
        >
          <Icon
            icon={parsedParams.isCollapsed ? "chevronRight" : "chevronLeft"}
          />
        </CollapseButton>
      )}
      <SidePanel
        ref={measureRef}
        style={{ ...style, height }}
        $isCollapsed={isCollapsed}
        $isScrollable={isScrollable}
        $hasTree={hasTree}
      >
        {isCollapsed ? null : children}
      </SidePanel>
    </>
  );
}

type MainPanelProps = {
  showScrollX?: boolean;
};
export function MainPanelLayout({
  children,
  showScrollX,
}: PropsWithChildren<MainPanelProps>) {
  const [measureRef, { top }] = useMeasure();
  const height = window.innerHeight - top - 1;
  return (
    <MainPanel
      style={{ height }}
      $isScrollXAllowed={showScrollX ?? allowScrollX[getActiveRoute()]}
      ref={measureRef}
    >
      {children}
    </MainPanel>
  );
}

const transition = "all 0.15s ease-out allow-discrete";
const CollapseButton = styled(Button)`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(p) => p.theme.colors.mutedNeutralBackground};
  width: ${COLLAPSE_BUTTON_WIDTH}px;
  height: ${COLLAPSE_BUTTON_HEIGHT}px;
  top: 250px;
  border: 1px solid ${(p) => p.theme.colors.lightNeutral};
  border-radius: 30px;
  z-index: 1000;
  box-shadow: 1px 1px 2px ${(p) => p.theme.colors.black}26;
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-height: 100vh;
  width: 100%;
  background-color: ${(p) => p.theme.colors.mutedNeutralBackground};
  overflow-x: hidden;
  overflow-y: hidden;
  @supports (-webkit-touch-callout: none) {
    height: -webkit-fill-available;
  }
`;
/*const Layout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background-color: ${(p) => p.theme.colors.mutedNeutralBackground};
  @supports (-webkit-touch-callout: none) {
    height: -webkit-fill-available;
  }
  overflow-x: hidden;
`;*/

const Border = styled.div<{ $showBorder?: boolean }>`
  position: sticky;
  border-right: ${(p) =>
    p.$showBorder ? `1px solid ${p.theme.colors.lightNeutral}` : "none"};
  z-index: 999;
`;

const Main = styled.main`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
  width: 100%;
  margin: 0 auto;
  background-color: ${(p) => p.theme.colors.white};
  @media (max-width: ${(p) => p.theme.breakpoints.tablet.max}px) {
    flex-wrap: nowrap;
  }
`;

const MainPanel = styled.div<{ $isScrollXAllowed: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${(p) => p.theme.space("medium")};
  gap: ${(p) => p.theme.space("normal")};
  overflow-y: auto;
`;

const SidePanel = styled(ScrollableContainer)<{
  $isScrollable?: boolean;
  $isCollapsed?: boolean;
  $hasTree?: boolean;
}>`
  display: flex;
  flex-direction: column;
  margin-top: ${(p) => p.theme.space("medium")};
  padding-left: ${(p) => p.theme.space("small")};
  padding-right: ${(p) => p.theme.space("xxsmall")};
  gap: ${(p) => p.theme.space("medium")};
  // NOTE: The new tree works in a way that it should never need
  // to resize the side panel, thats why we should be getting rid
  // of horizontal scrollbars in the side panel for better UX
  max-width: 340px;
  width: 340px;
  overflow-x: visible;
  overflow-y: hidden;
  white-space: nowrap;
  transition: ${transition};
  ${(p) =>
    p.$isCollapsed === true &&
    css`
      overflow-x: hidden;
      width: ${COLLAPSE_BUTTON_WIDTH}px;
      max-width: ${COLLAPSE_BUTTON_WIDTH}px;
      min-width: ${COLLAPSE_BUTTON_WIDTH}px;
    `}
  ${(p) =>
    p.$isScrollable === true &&
    css`
      overflow-y: ${p.$isCollapsed ? "hidden" : "auto"};
      // Calculate the height of the scrollable portion
      // 72px is the height of top navigation bar
      // TODO: Instead of hardcoding the height of the navigation bar maybe
      //       get a ref to the navigation bar and find out the height from there
      background-clip: padding-box;
    `}
  ${(p) =>
    p.$isCollapsed !== true &&
    css`
      @media (max-width: ${(p) => p.theme.breakpoints.tablet.max}px) {
        width: 65%;
      }
    `}
  ${(p) =>
    p.$hasTree &&
    css`
      padding: 0;
      margin: 0;
    `}
`;
